import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { bomberosActaAPI, bomberosCompaniaAPI, bomberosPersonalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft,
    FileText,
    Calendar,
    Clock,
    MapPin,
    Users,
    CheckCircle,
    XCircle,
    Building2,
    User,
    Printer,
} from 'lucide-react';

const TIPOS_ACTA = {
    entrenamiento: { label: 'Entrenamiento / Capacitación', color: 'bg-blue-100 text-blue-700' },
    guardia: { label: 'Guardia de Compañía', color: 'bg-green-100 text-green-700' },
    academia: { label: 'Academia', color: 'bg-purple-100 text-purple-700' },
    emergencia: { label: 'Emergencia / Acto de Servicio', color: 'bg-red-100 text-red-700' },
    reunion: { label: 'Reunión', color: 'bg-yellow-100 text-yellow-700' },
    inspeccion: { label: 'Inspección de Equipos', color: 'bg-orange-100 text-orange-700' },
    otro: { label: 'Otro', color: 'bg-gray-100 text-gray-700' },
};

const EVALUACION_CONFIG = {
    excelente: { label: 'Excelente', color: 'bg-green-500' },
    bueno: { label: 'Bueno', color: 'bg-blue-500' },
    regular: { label: 'Regular', color: 'bg-yellow-500' },
    deficiente: { label: 'Deficiente', color: 'bg-red-500' },
};

export default function ActaDetallePage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [acta, setActa] = useState(null);
    const [compania, setCompania] = useState(null);
    const [responsable, setResponsable] = useState(null);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const actaRes = await bomberosActaAPI.getById(id);
            setActa(actaRes.data);
            
            // Load compania and responsable
            if (actaRes.data.compania_id) {
                try {
                    const compRes = await bomberosCompaniaAPI.getAll();
                    const comp = compRes.data.find(c => c.id === actaRes.data.compania_id);
                    setCompania(comp);
                } catch (e) {}
            }
            
            if (actaRes.data.responsable_id) {
                try {
                    const bombRes = await bomberosPersonalAPI.getAll();
                    const resp = bombRes.data.find(b => b.id === actaRes.data.responsable_id);
                    setResponsable(resp);
                } catch (e) {}
            }
        } catch (err) {
            console.error('Error loading acta:', err);
            toast.error('Error al cargar el acta');
            navigate('/bomberos/actas');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const getTipoConfig = (tipo) => {
        return TIPOS_ACTA[tipo] || TIPOS_ACTA.otro;
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (!acta) return null;

    const tipoConfig = getTipoConfig(acta.tipo);
    const asistentes = acta.asistentes || [];
    const presentes = asistentes.filter(a => a.presente).length;
    const ausentes = asistentes.length - presentes;

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-5xl mx-auto">
            {/* Header - No print */}
            <div className="flex items-center justify-between print:hidden">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/actas')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Badge className={tipoConfig.color}>{tipoConfig.label}</Badge>
                        </div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <FileText className="h-6 w-6 text-blue-500" />
                            {acta.titulo}
                        </h1>
                    </div>
                </div>
                <Button variant="outline" onClick={handlePrint}>
                    <Printer className="h-4 w-4 mr-2" />
                    Imprimir
                </Button>
            </div>

            {/* Print Header */}
            <div className="hidden print:block text-center mb-6">
                <h1 className="text-2xl font-bold">ACTA DE ACTIVIDAD</h1>
                <p className="text-lg">{acta.titulo}</p>
                <Badge className={tipoConfig.color}>{tipoConfig.label}</Badge>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
                <Card className="border">
                    <CardContent className="pt-4 pb-4 text-center">
                        <Calendar className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                        <p className="text-lg font-bold">{acta.fecha}</p>
                        <p className="text-xs text-muted-foreground">Fecha</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="pt-4 pb-4 text-center">
                        <Clock className="h-6 w-6 mx-auto mb-2 text-orange-500" />
                        <p className="text-lg font-bold">{acta.hora_inicio} - {acta.hora_fin}</p>
                        <p className="text-xs text-muted-foreground">Horario</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="pt-4 pb-4 text-center">
                        <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-500" />
                        <p className="text-lg font-bold">{presentes}</p>
                        <p className="text-xs text-muted-foreground">Presentes</p>
                    </CardContent>
                </Card>
                <Card className="border">
                    <CardContent className="pt-4 pb-4 text-center">
                        <XCircle className="h-6 w-6 mx-auto mb-2 text-red-500" />
                        <p className="text-lg font-bold">{ausentes}</p>
                        <p className="text-xs text-muted-foreground">Ausentes</p>
                    </CardContent>
                </Card>
            </div>

            {/* Details */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle>Información de la Actividad</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Compañía</p>
                                    <p className="font-medium">{compania?.nombre || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Responsable / Instructor</p>
                                    <p className="font-medium">{responsable?.nombre_completo || 'N/A'}</p>
                                    {responsable?.grado && (
                                        <p className="text-sm text-muted-foreground">{responsable.grado}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Lugar</p>
                                    <p className="font-medium">{acta.lugar || 'No especificado'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {acta.descripcion && (
                                <div>
                                    <p className="text-sm text-muted-foreground">Descripción</p>
                                    <p className="whitespace-pre-line">{acta.descripcion}</p>
                                </div>
                            )}
                            {acta.observacion_general && (
                                <div>
                                    <p className="text-sm text-muted-foreground">Observaciones Generales</p>
                                    <p className="whitespace-pre-line">{acta.observacion_general}</p>
                                </div>
                            )}
                            {acta.firma_responsable && (
                                <div className="flex items-center gap-2 text-green-600">
                                    <CheckCircle className="h-4 w-4" />
                                    <span className="text-sm">Firmada por el responsable</span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Attendance Table */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Control de Asistencia
                    </CardTitle>
                    <CardDescription>
                        {presentes} de {asistentes.length} bomberos presentes ({Math.round((presentes / asistentes.length) * 100 || 0)}%)
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {asistentes.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No hay asistencia registrada</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Bombero</TableHead>
                                    <TableHead className="w-24">Asistencia</TableHead>
                                    <TableHead className="w-32">Evaluación</TableHead>
                                    <TableHead>Observación</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {asistentes.map((asistente, idx) => (
                                    <TableRow key={asistente.bombero_id || idx}>
                                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                                        <TableCell className="font-medium">{asistente.nombre || 'N/A'}</TableCell>
                                        <TableCell>
                                            {asistente.presente ? (
                                                <Badge className="bg-green-500">
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                    Presente
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-red-500">
                                                    <XCircle className="h-3 w-3 mr-1" />
                                                    Ausente
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {asistente.evaluacion && EVALUACION_CONFIG[asistente.evaluacion] ? (
                                                <Badge className={EVALUACION_CONFIG[asistente.evaluacion].color}>
                                                    {EVALUACION_CONFIG[asistente.evaluacion].label}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                                            {asistente.observacion || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Print Footer */}
            <div className="hidden print:block mt-8 pt-4 border-t">
                <div className="flex justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">Fecha de impresión: {new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                        <div className="border-t border-black w-48 mt-12 pt-2">
                            <p className="text-sm">Firma del Responsable</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
